import { FloatingTree } from "@floating-ui/react";
import { useCallback, useId, useMemo, useRef } from "react";
import { useControlledValue } from "../../hooks";
import { useTranslation } from "../../i18n";
import { cn, Slot } from "../../utils";
import { Dropdown } from "../Dropdown";
import { MenuContent } from "./MenuContent";
import { MenuContext } from "./context";
import { isMenuOption, type MenuProps } from "./types";

export const Menu = (props: MenuProps) => {
  const {
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    defaultIsOpen,
    trigger,
    children,
    options,
    onSelect,
    placement = "bottom-start",
    offset = 8,
    contentClassName,
    preventDefaultOnTrigger,
    stopPropagationOnTrigger,
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
    // Dropdown-specific props
    flip,
    shift,
    autoPlacement,
    middleware,
    clickOptions,
    hoverOptions,
    focusOptions,
    dismissOptions,
    roleOptions,
    listNavigationOptions: listNavigationOptionsProp,
    typeaheadOptions,
    floatingFocusManager: floatingFocusManagerProp,
    ariaLabel,
    triggerId: propsTriggerId,
    contentId: propsContentId,
    matchTriggerWidth,
    typeaheadListRef: propsTypeaheadListRef,
    disabled,
    portal,
    // Forward remaining props (from parent wrappers like Tooltip) to trigger
    ...forwardedProps
  } = props;

  const { rtl } = useTranslation();

  const { value: open, onChange: onOpenChange } = useControlledValue<boolean>({
    controlledValue: controlledOpen,
    controlledOnChange: controlledOnOpenChange,
    defaultValue: defaultIsOpen,
  });

  const rootTriggerRef = useRef<HTMLElement | null>(null);
  const generatedId = useId();
  const triggerId =
    propsTriggerId || children.props.id || `${generatedId}-trigger`;
  const contentId =
    propsContentId ||
    (children.props.id
      ? `${children.props.id}-content`
      : `${generatedId}-content`);

  const typeaheadListRef = useRef<(string | null)[]>([]);
  useMemo(() => {
    typeaheadListRef.current = options
      .filter(isMenuOption)
      .map((option) => option.text);
  }, [options]);

  const triggerElementBase = trigger || children;

  const handleTriggerRef = useCallback((node: HTMLElement | null) => {
    rootTriggerRef.current = node;
  }, []);

  const triggerElement = (
    <Slot ref={handleTriggerRef} {...forwardedProps}>
      {triggerElementBase}
    </Slot>
  );

  return (
    <FloatingTree>
      <MenuContext.Provider
        value={{ onSelect, options, rtl, rootTriggerRef, portal }}
      >
        <Dropdown
          trigger={triggerElement}
          content={<MenuContent options={options} />}
          triggerId={triggerId}
          contentId={contentId}
          open={open}
          onOpenChange={onOpenChange}
          interactions={["click", "dismiss", "listNavigation", "typeahead"]}
          role="menu"
          placement={placement}
          offset={offset}
          floatingFocusManager={{
            initialFocus: -1,
            returnFocus: true,
            restoreFocus: false,
            modal: false,
            ...floatingFocusManagerProp,
          }}
          contentClassName={cn("", contentClassName)}
          listNavigationOptions={{
            parentOrientation: "vertical",
            rtl,
            loop: true,
            ...listNavigationOptionsProp,
          }}
          typeaheadListRef={propsTypeaheadListRef || typeaheadListRef}
          preventDefaultOnTrigger={preventDefaultOnTrigger}
          stopPropagationOnTrigger={stopPropagationOnTrigger}
          minWidth={minWidth}
          maxWidth={maxWidth}
          minHeight={minHeight}
          maxHeight={maxHeight}
          flip={flip}
          shift={shift}
          autoPlacement={autoPlacement}
          middleware={middleware}
          clickOptions={clickOptions}
          hoverOptions={hoverOptions}
          focusOptions={focusOptions}
          dismissOptions={dismissOptions}
          roleOptions={roleOptions}
          typeaheadOptions={typeaheadOptions}
          ariaLabel={ariaLabel}
          matchTriggerWidth={matchTriggerWidth}
          disabled={disabled}
          portal={portal}
        />
      </MenuContext.Provider>
    </FloatingTree>
  );
};
